import React, { useState, useEffect } from 'react';
import { contractorsAPI } from '../services/api';
import './Contractors.css';

interface Contractor {
  name: string;
  services: string[];
  contact: {
    phone: string;
    email: string;
    website: string;
    address: string;
  };
  experience: string;
  certifications: string[];
}

interface Category {
  name: string;
  description: string;
  contractors: Contractor[];
}

interface Resource {
  name: string;
  contact: {
    phone: string;
    email: string;
    website: string;
    address: string;
  };
  services: string[];
}

interface Resources {
  government: Resource[];
  training: Resource[];
  associations: Resource[];
}

const Contractors: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [resources, setResources] = useState<Resources | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'contractors' | 'resources'>('contractors');

  useEffect(() => {
    fetchContractorsData();
  }, []);

  const fetchContractorsData = async () => {
    try {
      setLoading(true);
      const [categoriesRes, resourcesRes] = await Promise.all([
        contractorsAPI.getCategories(),
        contractorsAPI.getResources()
      ]);

      setCategories(categoriesRes.data.data);
      setResources(resourcesRes.data.data);
    } catch (err) {
      setError('Error loading contractors data. Please try again later.');
      console.error('Error fetching contractors:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleWebsite = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  if (loading) {
    return (
      <div className="contractors-page">
        <div className="container">
          <div className="loading">
            <div className="spinner"></div>
            <p>Loading contractors and resources...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="contractors-page">
        <div className="container">
          <div className="error">
            <h2>Error Loading Contractors Data</h2>
            <p>{error}</p>
            <button onClick={fetchContractorsData} className="btn btn-primary">
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="contractors-page">
      <div className="container">
        {/* Header */}
        <div className="contractors-header">
          <h1>Contractors & Resources</h1>
          <p>Find contractors, service providers, and resources for Guyana's oil and gas operations</p>
        </div>

        {/* Tab Navigation */}
        <div className="tab-navigation">
          <button
            className={`tab-button ${activeTab === 'contractors' ? 'active' : ''}`}
            onClick={() => setActiveTab('contractors')}
          >
            Contractors
          </button>
          <button
            className={`tab-button ${activeTab === 'resources' ? 'active' : ''}`}
            onClick={() => setActiveTab('resources')}
          >
            Resources
          </button>
        </div>

        {/* Contractors Tab */}
        {activeTab === 'contractors' && (
          <div className="contractors-content">
            {categories.map((category, index) => (
              <section key={index} className="category-section">
                <h2>{category.name}</h2>
                <p className="category-description">{category.description}</p>
                
                <div className="contractors-grid">
                  {category.contractors.map((contractor, idx) => (
                    <div key={idx} className="contractor-card">
                      <h3>{contractor.name}</h3>
                      <p className="experience">{contractor.experience}</p>
                      
                      <div className="services">
                        <h4>Services:</h4>
                        <ul>
                          {contractor.services.map((service, serviceIdx) => (
                            <li key={serviceIdx}>{service}</li>
                          ))}
                        </ul>
                      </div>

                      <div className="certifications">
                        <h4>Certifications:</h4>
                        <div className="cert-tags">
                          {contractor.certifications.map((cert, certIdx) => (
                            <span key={certIdx} className="cert-tag">{cert}</span>
                          ))}
                        </div>
                      </div>

                      <div className="contact-info">
                        <h4>Contact Information:</h4>
                        <p><strong>Phone:</strong> {contractor.contact.phone}</p>
                        <p><strong>Email:</strong> {contractor.contact.email}</p>
                        <p><strong>Address:</strong> {contractor.contact.address}</p>
                      </div>

                      <button
                        onClick={() => handleWebsite(contractor.contact.website)}
                        className="btn btn-primary"
                      >
                        Visit Website
                      </button>
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}

        {/* Resources Tab */}
        {activeTab === 'resources' && resources && (
          <div className="resources-content">
            {/* Government Resources */}
            <section className="resource-section">
              <h2>Government Resources</h2>
              <div className="resources-grid">
                {resources.government.map((resource, index) => (
                  <div key={index} className="resource-card">
                    <h3>{resource.name}</h3>
                    <div className="services">
                      <h4>Services:</h4>
                      <ul>
                        {resource.services.map((service, idx) => (
                          <li key={idx}>{service}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="contact-info">
                      <h4>Contact Information:</h4>
                      <p><strong>Phone:</strong> {resource.contact.phone}</p>
                      <p><strong>Email:</strong> {resource.contact.email}</p>
                      <p><strong>Address:</strong> {resource.contact.address}</p>
                    </div>
                    <button
                      onClick={() => handleWebsite(resource.contact.website)}
                      className="btn btn-primary"
                    >
                      Visit Website
                    </button>
                  </div>
                ))}
              </div>
            </section>

            {/* Training Resources */}
            <section className="resource-section">
              <h2>Training & Education</h2>
              <div className="resources-grid">
                {resources.training.map((resource, index) => (
                  <div key={index} className="resource-card">
                    <h3>{resource.name}</h3>
                    <div className="services">
                      <h4>Services:</h4>
                      <ul>
                        {resource.services.map((service, idx) => (
                          <li key={idx}>{service}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="contact-info">
                      <h4>Contact Information:</h4>
                      <p><strong>Phone:</strong> {resource.contact.phone}</p>
                      <p><strong>Email:</strong> {resource.contact.email}</p>
                      <p><strong>Address:</strong> {resource.contact.address}</p>
                    </div>
                    <button
                      onClick={() => handleWebsite(resource.contact.website)}
                      className="btn btn-primary"
                    >
                      Visit Website
                    </button>
                  </div>
                ))}
              </div>
            </section>

            {/* Associations */}
            <section className="resource-section">
              <h2>Industry Associations</h2>
              <div className="resources-grid">
                {resources.associations.map((resource, index) => (
                  <div key={index} className="resource-card">
                    <h3>{resource.name}</h3>
                    <div className="services">
                      <h4>Services:</h4>
                      <ul>
                        {resource.services.map((service, idx) => (
                          <li key={idx}>{service}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="contact-info">
                      <h4>Contact Information:</h4>
                      <p><strong>Phone:</strong> {resource.contact.phone}</p>
                      <p><strong>Email:</strong> {resource.contact.email}</p>
                      <p><strong>Address:</strong> {resource.contact.address}</p>
                    </div>
                    <button
                      onClick={() => handleWebsite(resource.contact.website)}
                      className="btn btn-primary"
                    >
                      Visit Website
                    </button>
                  </div>
                ))}
              </div>
            </section>
          </div>
        )}
      </div>
    </div>
  );
};

export default Contractors;
